import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpenText, NotebookPen, CalendarDays, ArrowRight, Sparkles, FileDown } from "lucide-react";
import api, { API } from "../lib/api";
import { auth } from "../lib/firebase";
import { Spinner } from "../components/common";

/* My Travel Diaries — list of every diary the user created (per-trip travel
   diaries + memoirs), with the "Why Keep Travel Diaries and Memoirs" article link on top. */
export default function TravelDiariesList() {
  const [data, setData] = useState(null);
  const [tok, setTok] = useState(null);

  useEffect(() => {
    api.get("/memoirs/diaries-overview").then((r) => setData(r.data)).catch(() => setData({ items: [], memoir_count: 0 }));
    auth.currentUser?.getIdToken().then(setTok).catch(() => {});
  }, []);

  if (!data) return <Spinner full />;

  const downloadPdf = async (e, tripId) => {
    e.preventDefault();
    e.stopPropagation();
    const token = await auth.currentUser?.getIdToken();
    window.open(`${API}/diary/${tripId}/pdf?auth=${token}`, "_blank");
  };

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10" data-testid="travel-diaries-page">
      <Link to="/memoirs" data-testid="diaries-article-link"
        className="flex items-center gap-2 border border-indigo-300 bg-indigo-50/70 px-4 py-3 mb-8 text-sm font-700 text-indigo-800 hover:bg-indigo-100 transition-colors">
        <Sparkles className="w-4 h-4" /> Why Keep Travel Diaries and Memoirs? Read the story behind My Memoirs <ArrowRight className="w-4 h-4 ml-auto" />
      </Link>

      <h1 className="font-heading font-900 text-4xl flex items-center gap-3 mb-1">
        <BookOpenText className="w-9 h-9 text-indigo-700" /> My Travel Diaries — My Memoirs
      </h1>
      <p className="text-sm italic text-indigo-900/70 mb-8">Every Moment Has a Story. Keep Yours.</p>

      <Link to="/memoirs" data-testid="diaries-memoirs-card"
        className="block border-2 border-indigo-200 bg-indigo-50/50 p-4 mb-6 hover:border-indigo-400 transition-colors">
        <div className="flex items-center gap-3">
          <NotebookPen className="w-6 h-6 text-indigo-700" />
          <div>
            <div className="font-heading font-900 text-lg">My Memoirs</div>
            <div className="text-sm text-muted-foreground">{data.memoir_count} moment{data.memoir_count === 1 ? "" : "s"} kept — hikes, picnics, celebrations and everyday memories</div>
          </div>
          <ArrowRight className="w-5 h-5 ml-auto text-indigo-600" />
        </div>
      </Link>

      <h2 className="font-heading font-900 text-xl mb-3">Travel Journey Diaries</h2>
      {data.items.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-8 text-center space-y-3" data-testid="diaries-empty">
          <p>No travel diaries yet — open a trip and start writing in its <b>Traveler Diary</b>.</p>
          <Link to="/travel-hub#hub-trips" data-testid="diaries-empty-trips-link"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 text-white bg-indigo-600 hover:bg-indigo-700 transition-colors">
            Open My Trips <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="diaries-list">
          {data.items.map((d) => (
            <Link key={d.trip_id} to={`/trips/${d.trip_id}/diary`} data-testid={`diary-card-${d.trip_id}`}
              className="flex gap-3 border border-zinc-300 bg-white p-4 hover:border-indigo-400 transition-colors">
              {d.photo_url && tok && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${d.photo_url}?auth=${tok}`} alt="" data-testid={`diary-thumb-${d.trip_id}`}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  className="w-20 h-20 object-cover border border-zinc-200 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-heading font-900 text-lg">{d.title}</span>
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" /> Started {d.started}{d.last_entry && d.last_entry !== d.started ? ` · last entry ${d.last_entry}` : ""}
                </span>
                <span className="text-[11px] font-700 bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded ml-auto">
                  {d.entry_count} entr{d.entry_count === 1 ? "y" : "ies"}
                </span>
              </div>
              {d.snippet && <p className="text-sm text-zinc-700 mt-1 line-clamp-2">{d.snippet}…</p>}
              <span className="flex items-center gap-4 mt-1.5">
                <span className="text-xs font-700 text-indigo-700 inline-flex items-center gap-1">Open diary <ArrowRight className="w-3.5 h-3.5" /></span>
                <button onClick={(e) => downloadPdf(e, d.trip_id)} data-testid={`diary-pdf-${d.trip_id}`}
                  className="text-xs font-700 text-zinc-500 hover:text-indigo-700 inline-flex items-center gap-1">
                  <FileDown className="w-3.5 h-3.5" /> Keepsake PDF
                </button>
              </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
